/**
 * Pure layout model for the Orientation Editor's floating workspace: the map
 * fills the editor, the Stops / Inspector panels float over it as draggable,
 * resizable cards, and the timeline is a bottom drawer. This module owns the
 * geometry rules (defaults, clamping, persistence) and the list-reorder math
 * shared by the drag interactions. No React state so the rules stay testable.
 */

export type FloatKey = 'stops' | 'inspector';
export type Rect = { x: number; y: number; w: number; h: number };
export type CanvasSize = { w: number; h: number };

export type EditorLayout = {
  /** `docked`: the card is pinned as a full-height column on its edge
   *  (stops left, inspector right) and pushes the map instead of floating. */
  panels: Record<FloatKey, { rect: Rect; collapsed: boolean; docked: boolean }>;
  /** `docked`: the drawer is pinned to the bottom edge at full width and the
   *  map and docked side columns stop above it. */
  timeline: { h: number; collapsed: boolean; docked: boolean };
};

/** How much of a card must stay inside the canvas so it can't be lost. */
const MIN_VISIBLE = 80;
/** Height of a card's header bar (the drag handle) — kept fully on-canvas. */
const HEAD_H = 40;
/**
 * The map's own controls own a strip along the canvas top: the Leaflet zoom
 * control (top-left, ~10..44px) and the 3D / basemap segment row
 * (`.oe-map-controls`, top 10px, from x=52). Those live INSIDE the isolated
 * map layer, so a floating card can never be stacked below them — the only
 * way to keep them reachable is to not place cards on top of them by
 * default. Default (and reset) placement starts below this strip. Users can
 * still drag a card up over it deliberately.
 */
export const MAP_CONTROLS_STRIP_H = 56;

export const FLOAT_LIMITS: Record<FloatKey, { w: [number, number]; h: [number, number] }> = {
  stops: { w: [240, 480], h: [200, 900] },
  inspector: { w: [300, 620], h: [220, 900] },
};
export const DRAWER_LIMITS: [number, number] = [160, 620];

// v3 (2026-08-15): default placement moved below the map-controls strip. Bumping
// the key retires every persisted v2 layout (Stops card parked at y=12, over
// the zoom / basemap controls) so users get the corrected default once,
// without having to know about "Reset layout".
const STORAGE_KEY = 'oe_layout_v3';

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, Math.round(v)));

export const clampW = (k: FloatKey, v: number) => clamp(v, FLOAT_LIMITS[k].w[0], FLOAT_LIMITS[k].w[1]);
export const clampH = (k: FloatKey, v: number) => clamp(v, FLOAT_LIMITS[k].h[0], FLOAT_LIMITS[k].h[1]);

/** Drawer height: hard limits, capped so its header + top resize handle stay
 *  on-canvas (a taller persisted value would clip them irrecoverably). */
export function clampDrawerH(v: number, canvasH?: number): number {
  const [min, max] = DRAWER_LIMITS;
  const cap = canvasH != null ? Math.max(min, Math.min(max, Math.round(canvasH) - 60)) : max;
  return clamp(v, min, cap);
}

/** First-run (and Reset layout) placement: stops left, inspector right, both
 *  starting BELOW the map-controls strip so zoom / 3D / basemap stay
 *  clickable. Minimal by default — the map is the workspace: inspector and
 *  timeline start collapsed (selecting a stop expands them), the stops card
 *  hugs its content (its rect.h is a MAX height, see FloatPanel fitContent). */
export function defaultLayout(canvas: CanvasSize): EditorLayout {
  const stopsW = 260;
  const inspW = 360;
  const top = MAP_CONTROLS_STRIP_H;
  // Cap heights against the room actually left above the timeline drawer's
  // default footprint (220 + 10px margin).
  const room = Math.max(200, canvas.h - top - 230);
  return {
    panels: {
      stops: {
        rect: { x: 12, y: top, w: stopsW, h: clamp(Math.min(canvas.h * 0.45, room), 200, 620) },
        collapsed: false,
        docked: false,
      },
      inspector: {
        rect: {
          x: Math.max(12, canvas.w - inspW - 12),
          y: top,
          w: inspW,
          h: clamp(Math.min(canvas.h * 0.6, room), 220, 680),
        },
        collapsed: true,
        docked: false,
      },
    },
    timeline: { h: 220, collapsed: true, docked: false },
  };
}

/** Keep a card reachable: header on-canvas, at least MIN_VISIBLE px in view,
 *  and height capped to the canvas so the bottom resize handles stay on it. */
export function clampRect(k: FloatKey, r: Rect, canvas: CanvasSize): Rect {
  const w = clampW(k, r.w);
  const hCap = Math.max(FLOAT_LIMITS[k].h[0], Math.round(canvas.h) - 24);
  const h = Math.min(clampH(k, r.h), hCap);
  return {
    w,
    h,
    x: clamp(r.x, MIN_VISIBLE - w, Math.max(0, canvas.w - MIN_VISIBLE)),
    y: clamp(r.y, 0, Math.max(0, canvas.h - HEAD_H)),
  };
}

export function clampLayout(l: EditorLayout, canvas: CanvasSize): EditorLayout {
  return {
    panels: {
      stops: { ...l.panels.stops, rect: clampRect('stops', l.panels.stops.rect, canvas) },
      inspector: { ...l.panels.inspector, rect: clampRect('inspector', l.panels.inspector.rect, canvas) },
    },
    timeline: { ...l.timeline, h: clampDrawerH(l.timeline.h, canvas.h) },
  };
}

/** Null when nothing (or garbage) is stored — the caller then derives
 *  defaultLayout from the measured canvas. */
export function loadLayout(): EditorLayout | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<EditorLayout>;
    const rect = (k: FloatKey): Rect | null => {
      const r = p.panels?.[k]?.rect;
      if (!r) return null;
      const nums = [r.x, r.y, r.w, r.h];
      return nums.every((n) => typeof n === 'number' && Number.isFinite(n))
        ? { x: Math.round(r.x), y: Math.round(r.y), w: clampW(k, r.w), h: clampH(k, r.h) }
        : null;
    };
    const stops = rect('stops');
    const inspector = rect('inspector');
    if (!stops || !inspector) return null;
    return {
      panels: {
        stops: { rect: stops, collapsed: p.panels?.stops?.collapsed === true, docked: p.panels?.stops?.docked === true },
        inspector: { rect: inspector, collapsed: p.panels?.inspector?.collapsed === true, docked: p.panels?.inspector?.docked === true },
      },
      timeline: {
        h: clampDrawerH(typeof p.timeline?.h === 'number' && Number.isFinite(p.timeline.h) ? p.timeline.h : 280),
        collapsed: p.timeline?.collapsed === true,
        docked: p.timeline?.docked === true,
      },
    };
  } catch {
    return null;
  }
}

export function saveLayout(l: EditorLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(l));
  } catch {
    /* best-effort: private mode / quota just means the layout won't persist */
  }
}

export function clearLayout(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Move arr[from] to gap `insert` (0..length, in pre-removal coordinates),
 * returning a new array. Shared by stop-row and beat drag-reordering.
 */
export function moveItem<T>(arr: T[], from: number, insert: number): T[] {
  const next = [...arr];
  const [x] = next.splice(from, 1);
  next.splice(insert > from ? insert - 1 : insert, 0, x);
  return next;
}

/** A drop at gap `insert` that would leave `from` exactly where it is. */
export function isNoopMove(from: number, insert: number): boolean {
  return insert === from || insert === from + 1;
}
